import {
  clearToken,
  getToken,
  hydrateIdentity,
  resetIdentityForSwitch,
} from '../auth'
import {
  clearImpersonationTarget,
  getImpersonationTarget,
} from '../impersonation'
import { getUserManager } from '../oidc'
import { formatPermissionError } from '../permissions'
import { queryClient } from '../query-client'
import { ApiError, parseProblem } from './problem'

const SILENT_RENEW_BACKSTOP_MS = 12000

let renewInFlight: Promise<boolean> | null = null
let endingSession = false

export function tryRecoverAuth(): Promise<boolean> {
  const um = getUserManager()
  if (!um) return Promise.resolve(false)
  if (!renewInFlight) {
    const timeout = new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(false), SILENT_RENEW_BACKSTOP_MS),
    )
    renewInFlight = Promise.race([
      um
        .signinSilent()
        .then((user) => user != null)
        .catch(() => false),
      timeout,
    ]).finally(() => {
      renewInFlight = null
    })
  }
  return renewInFlight
}

function recoverBeforeRetry(signal?: AbortSignal | null): Promise<boolean> {
  if (!signal) return tryRecoverAuth()
  if (signal.aborted) return Promise.reject(signal.reason)
  return new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason)
    signal.addEventListener('abort', abort, { once: true })
    void tryRecoverAuth()
      .then(resolve, reject)
      .finally(() => {
        signal.removeEventListener('abort', abort)
      })
  })
}

async function endSession(): Promise<void> {
  if (endingSession) return
  endingSession = true
  const redirected = await clearToken().catch(() => false)
  if (!redirected && typeof window !== 'undefined') {
    window.location.assign('/auth/login')
  }
}

export async function send(
  path: string,
  init?: RequestInit,
  retrying = false,
): Promise<Response> {
  const token = getToken()
  const headers = new Headers(init?.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  const impersonationTarget = getImpersonationTarget()
  if (impersonationTarget) headers.set('X-Impersonation', impersonationTarget)

  const resp = await fetch(path, { ...init, headers })
  // Conditional reads intentionally return no body when the cached representation is current.
  if (resp.ok || resp.status === 304) return resp

  if (resp.status === 401) {
    if (!retrying && (await recoverBeforeRetry(init?.signal))) {
      return send(path, init, true)
    }
    await endSession()
  }
  const body = await resp.text()
  const problem = parseProblem(body)
  if (
    impersonationTarget &&
    problem?.code === 'IMPERSONATION_SESSION_INVALID'
  ) {
    clearImpersonationTarget()
    resetIdentityForSwitch()
    queryClient.clear()
    void hydrateIdentity()
  }
  if (resp.status === 403) {
    throw new ApiError(403, body, {
      ...(problem ?? {}),
      detail: formatPermissionError(problem),
    })
  }
  throw new ApiError(resp.status, body, problem)
}

export async function request<T>(
  path: string,
  decode: (raw: unknown) => T,
  init?: RequestInit,
): Promise<T> {
  const resp = await send(path, init)
  const raw: unknown = await resp.json()
  return decode(raw)
}

export async function requestAnonymous<T>(
  path: string,
  decode: (raw: unknown) => T,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  const response = await fetch(path, { ...init, headers })
  const body = await response.text()
  if (!response.ok)
    throw new ApiError(response.status, body, parseProblem(body))
  const raw: unknown = JSON.parse(body)
  return decode(raw)
}

export async function requestNoContent(
  path: string,
  init?: RequestInit,
): Promise<void> {
  await send(path, init)
}

export function json(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body: unknown,
): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }
}

export function withQuery(
  path: string,
  params: Record<string, string | undefined>,
): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value)
  }
  const qs = query.toString()
  return qs ? `${path}?${qs}` : path
}

const representationCache = new Map<string, { etag: string; value: unknown }>()

export async function conditionalRequest<T>(path: string, decode: (raw: unknown) => T): Promise<T> {
  const cached = representationCache.get(path)
  const response = await send(path, cached ? { headers: { 'If-None-Match': cached.etag } } : undefined)
  if (response.status === 304) {
    if (!cached) throw new Error('Server returned 304 without a cached representation')
    return cached.value as T
  }
  const value = decode(await response.json())
  const etag = response.headers.get('etag')
  if (etag) representationCache.set(path, { etag, value })
  return value
}

export function __resetApiForTests(): void {
  renewInFlight = null
  endingSession = false
  representationCache.clear()
}
